/**
 * displayName — shared display-name resolution for the farm twin explorer.
 *
 * Two jobs (design doc §3.4, bugs B1/B3/B4/B5):
 *
 * 1. `resolveDisplayTemplate` — renders a `feed_display` template
 *    ("bis {valid_to_display}", "{supplier.short_name} ↔ {buyer.short_name}")
 *    with COLLAPSE-EMPTY semantics: a token that resolves empty takes its
 *    owning literal with it, and a template whose tokens are ALL empty
 *    returns null. This is what kills "bis —", "— – —" and the lone "⇔".
 *
 * 2. `resolveEntityTitle` — the title chain for a row: template →
 *    row.name → identifying-attribute chain → typed fallback (never a
 *    bare dash).
 */

/** Values treated as empty when a template token resolves. */
export const EMPTY_TOKEN_VALUES: ReadonlySet<string> = new Set(['', '—', '–', '-', 'null', 'undefined']);

function resolvePath(path: string, data: Record<string, unknown>): unknown {
    return path.split('.').reduce<unknown>((obj, key) => {
        if (obj && typeof obj === 'object') return (obj as Record<string, unknown>)[key];
        return undefined;
    }, data);
}

export interface TemplateOptions {
    /**
     * Display names for `{ref:<key>}` tokens: the token reads `data[<key>]`
     * (an entity id) and substitutes `refNames[thatId]`. Missing map or id
     * → the token resolves empty (and collapses, never leaks a raw uuid).
     */
    refNames?: Record<string, string>;
}

/**
 * Render a `{token}`-template against a data object with collapse-empty
 * semantics:
 *
 * - each token owns the literal BEFORE it (up to the previous token);
 * - a segment renders only when its token resolves non-empty;
 * - the trailing literal (after the last token) renders only when at
 *   least one token rendered;
 * - all tokens empty → `null` (callers fall through their chain).
 *
 * A template without any tokens is returned verbatim (trimmed, or null
 * when blank).
 */
export function resolveDisplayTemplate(
    template: string,
    data: Record<string, unknown>,
    options?: TemplateOptions,
): string | null {
    const tokenRe = /\{([^}]+)\}/g;
    let lastIndex = 0;
    let out = '';
    let sawToken = false;
    let renderedToken = false;
    let match: RegExpExecArray | null;

    while ((match = tokenRe.exec(template)) !== null) {
        sawToken = true;
        const literal = template.slice(lastIndex, match.index);
        lastIndex = match.index + match[0].length;

        const expr = match[1];
        let raw: unknown;
        if (expr.startsWith('ref:')) {
            const key = expr.slice(4);
            const id = resolvePath(key, data);
            raw = id == null ? undefined : options?.refNames?.[String(id)];
        } else {
            raw = resolvePath(expr, data);
        }
        const value = raw == null ? '' : String(raw).trim();
        if (value && !EMPTY_TOKEN_VALUES.has(value)) {
            // When nothing rendered yet, a preceding collapsed token may
            // have left this literal starting with a separator ("· ", "– ")
            // — strip it; word prefixes ("bis ") survive.
            const cleanLiteral = out === '' ? literal.replace(/^[\s·•–—|,;:↔-]+/, '') : literal;
            out += cleanLiteral + value;
            renderedToken = true;
        }
        // Empty token: drop it AND its owning literal.
    }

    if (!sawToken) {
        const trimmed = template.trim();
        return trimmed.length > 0 ? trimmed : null;
    }
    if (!renderedToken) return null;

    const trailing = template.slice(lastIndex);
    const result = (out + trailing).trim();
    return result.length > 0 ? result : null;
}

/**
 * Identifying-attribute chain — mirrors the backend's
 * `_DISPLAY_NAME_KEYS` (twin_data_service) so list rows, object headers
 * and relation chips agree on what an entity is called.
 */
export const IDENTIFYING_KEYS = [
    'name',
    'full_name',
    'agreement_number',
    'parcel_number',
    'ear_tag',
    'title',
    // Document-ish numbers BEFORE descriptive attributes (mirrors the
    // backend _DISPLAY_NAME_KEYS): a settlement is "RW-99996", not its crop.
    'delivery_note_number',
    'delivery_number',
    'settlement_number',
    'number',
    'reference',
    'crop',
] as const;

export interface EntityTitle {
    title: string;
    /** True when no real identifier was found (typed placeholder shown). */
    isFallback: boolean;
}

export function shortId(id: string): string {
    return id.length > 8 ? id.slice(0, 8) : id;
}

/**
 * Title chain for an entity row: feed_display template (collapse-empty)
 * → identifying attributes → typed fallback. Never returns a bare dash
 * (bug B1) — nameless entities read "Ohne Bezeichnung · <id-prefix>".
 */
export function resolveEntityTitle(
    row: { id: string; name?: string; properties: Record<string, unknown> },
    template?: string | null,
    options?: TemplateOptions,
): EntityTitle {
    if (template) {
        const data: Record<string, unknown> = { ...row.properties, ...row, properties: row.properties };
        const fromTemplate = resolveDisplayTemplate(template, data, options);
        if (fromTemplate) return { title: fromTemplate, isFallback: false };
    }
    const name = row.name?.trim();
    if (name && !EMPTY_TOKEN_VALUES.has(name)) return { title: name, isFallback: false };
    for (const key of IDENTIFYING_KEYS) {
        const value = row.properties[key];
        if (typeof value === 'string' && value.trim() && !EMPTY_TOKEN_VALUES.has(value.trim())) {
            return { title: value.trim(), isFallback: false };
        }
        if (typeof value === 'number') return { title: String(value), isFallback: false };
    }
    // Person rows often carry only the name parts.
    const first = row.properties.first_name;
    const last = row.properties.last_name;
    const composite = [first, last]
        .filter((v): v is string => typeof v === 'string' && !EMPTY_TOKEN_VALUES.has(v.trim()))
        .map((v) => v.trim())
        .join(' ');
    if (composite) return { title: composite, isFallback: false };
    return { title: `Ohne Bezeichnung · ${shortId(row.id)}`, isFallback: true };
}
